/**
 * cardPayment — shared helpers for the web card (Visa/Mastercard) flow.
 *
 * WHY sessionStorage: the place-order screens navigate to /pay/authorize with
 * window.location.href (full reload), so redux state does NOT survive into the
 * PIN/OTP pages. We stash a SANITIZED context (last4 + brand + amount + start
 * time — NEVER the PAN or CVV) so the authorize step can show the customer
 * what they are authorizing, and a countdown that mirrors the server's 15-min
 * cached-charge TTL (redis `web_client_payload`, see flutterWaveController).
 */
import { toast } from "react-toastify";

const KEY = "cardAuthCtx";

/** Mirrors the backend's 15-minute cached charge payload TTL. */
export const CARD_AUTH_TTL_MS = 15 * 60 * 1000;

function brandFromNumber(digits) {
  if (/^4/.test(digits)) return "Visa";
  if (/^(5[1-5]|2[2-7])/.test(digits)) return "Mastercard";
  if (/^3[47]/.test(digits)) return "American Express";
  return "Card";
}

/** Store sanitized authorize-page context. Safe to call with partial data. */
export function stashCardAuthContext(cardDetails, totalPrice) {
  try {
    const digits = String(cardDetails?.cardNumber || "").replace(/\D/g, "");
    sessionStorage.setItem(
      KEY,
      JSON.stringify({
        last4: digits.slice(-4),
        brand: brandFromNumber(digits),
        amount: Number(totalPrice) || null,
        startedAt: Date.now(),
      })
    );
  } catch {
    /* storage unavailable — context display is cosmetic, never block payment */
  }
}

export function readCardAuthContext() {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearCardAuthContext() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
}

/**
 * Navigate to a payment redirect URL. External URLs (3-D Secure bank pages,
 * Flutterwave-hosted OTP capture) get a brief heads-up toast first so the
 * hand-off doesn't feel like a hijack; internal routes navigate immediately.
 */
export function followCardRedirect(url) {
  if (!url) return;
  let external = false;
  try {
    external = /^https?:\/\//i.test(url) && new URL(url).origin !== window.location.origin;
  } catch {
    external = false;
  }
  if (external) {
    toast.info("Taking you to a secure verification page…", { autoClose: 2200 });
    setTimeout(() => {
      window.location.href = url;
    }, 1300);
  } else {
    window.location.href = url;
  }
}

const cardPayment = {
  CARD_AUTH_TTL_MS,
  stashCardAuthContext,
  readCardAuthContext,
  clearCardAuthContext,
  followCardRedirect,
};
export default cardPayment;
